import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Lock,
  PersonAdd,
} from "@mui/icons-material";
import { signUp, confirmSignUp, signIn } from "aws-amplify/auth";
import { AtomicLogo } from "../../../assets/AtomicLogo";

interface PasswordCreationProps {
  email: string;
  onSwitchToLanding: () => void;
  onSwitchToPasswordEntry: () => void;
  onSignUpSuccess: () => void;
}

export const PasswordCreationComponent = ({
  email,
  onSwitchToLanding,
  onSwitchToPasswordEntry,
  onSignUpSuccess,
}: PasswordCreationProps) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const validatePassword = (password: string) => {
    const minLength = 12;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return {
      isValid:
        password.length >= minLength &&
        hasUpperCase &&
        hasLowerCase &&
        hasNumbers &&
        hasSpecialChar,
      errors: [
        password.length < minLength ? `At least ${minLength} characters` : null,
        !hasUpperCase ? "At least one uppercase letter" : null,
        !hasLowerCase ? "At least one lowercase letter" : null,
        !hasNumbers ? "At least one number" : null,
        !hasSpecialChar ? "At least one special character" : null,
      ].filter(Boolean),
    };
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Validate password
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      setError(
        "Password must meet the following requirements:\n" +
          passwordValidation.errors.join(", ")
      );
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    try {
      const { isSignUpComplete } = await signUp({
        username: email,
        password: password,
        options: {
          userAttributes: {
            email: email,
          },
        },
      });

      if (isSignUpComplete) {
        onSignUpSuccess();
      } else {
        setNeedsVerification(true);
      }
    } catch (err: any) {
      console.error("Sign up error:", err);

      if (err.name === "UsernameExistsException") {
        // User already exists - redirect to password entry instead of showing error
        console.log("User already exists, redirecting to password entry");
        onSwitchToPasswordEntry();
        return;
      } else if (err.name === "InvalidPasswordException") {
        setError("Password does not meet requirements.");
      } else {
        setError(
          err.message || "An error occurred during sign up. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerifying(true);
    setError("");

    try {
      await confirmSignUp({
        username: email,
        confirmationCode: verificationCode,
      });

      // After successful verification, automatically sign in the user
      try {
        await signIn({
          username: email,
          password: password, // Use the password they entered during signup
        });
        onSignUpSuccess();
      } catch (signInError: any) {
        console.error("Auto sign-in after verification failed:", signInError);
        // If auto sign-in fails, still call success to let the user know verification worked
        // They can sign in manually if needed
        onSignUpSuccess();
      }
    } catch (err: any) {
      console.error("Verification error:", err);

      if (err.name === "CodeMismatchException") {
        setError(
          "Invalid verification code. Please check your email and try again."
        );
      } else if (err.name === "ExpiredCodeException") {
        setError("Verification code has expired. Please request a new one.");
      } else {
        setError(
          err.message ||
            "An error occurred during verification. Please try again."
        );
      }
    } finally {
      setVerifying(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const passwordValidation = validatePassword(password);

  if (needsVerification) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: "#fafafa",
          padding: 2,
        }}
      >
        <Card
          sx={{
            maxWidth: 400,
            width: "100%",
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
          }}
        >
          <CardContent sx={{ padding: 4 }}>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Box sx={{ mb: 2 }}>
                <AtomicLogo width={64} height={64} />
              </Box>
              <Typography variant="h4" component="h1" gutterBottom>
                Verify Your Email
              </Typography>
              <Typography variant="body2" color="text.secondary">
                We've sent a verification code to {email}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleConfirmSignUp}>
              <TextField
                fullWidth
                label="Verification Code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                required
                margin="normal"
                placeholder="Enter the 6-digit code"
                sx={{ mb: 2 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={verifying || !verificationCode}
                startIcon={
                  verifying ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : undefined
                }
                sx={{
                  mb: 2,
                  py: 1.5,
                  backgroundColor: "#3877F7",
                  "&:hover": {
                    backgroundColor: "#2d5fd6",
                  },
                }}
              >
                {verifying ? "Verifying..." : "Verify Email"}
              </Button>

              <Box sx={{ textAlign: "center" }}>
                <Button
                  component="button"
                  type="button"
                  variant="text"
                  onClick={() => setNeedsVerification(false)}
                  sx={{ textDecoration: "none" }}
                >
                  ← Back to password creation
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        background: "#fafafa",
        padding: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 400,
          width: "100%",
          borderRadius: 2,
          border: "1px solid #e0e0e0",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
        }}
      >
        <CardContent sx={{ padding: 4 }}>
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box sx={{ mb: 2 }}>
              <AtomicLogo width={64} height={64} />
            </Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Create your account
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Set a password for {email}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSignUp}>
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={togglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />

            {password && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: "block" }}
                >
                  Password must contain:
                </Typography>
                {passwordValidation.errors.map((error, index) => (
                  <Typography
                    key={index}
                    variant="caption"
                    color={
                      passwordValidation.isValid ? "success.main" : "error.main"
                    }
                    sx={{ display: "block", fontSize: "0.75rem" }}
                  >
                    • {error}
                  </Typography>
                ))}
              </Box>
            )}

            <TextField
              fullWidth
              label="Confirm Password"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={toggleConfirmPasswordVisibility}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={
                loading ||
                !password ||
                !confirmPassword ||
                !passwordValidation.isValid
              }
              startIcon={
                loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <PersonAdd />
                )
              }
              sx={{
                mb: 2,
                py: 1.5,
                backgroundColor: "#3877F7",
                "&:hover": {
                  backgroundColor: "#2d5fd6",
                },
              }}
            >
              {loading ? "Creating Account..." : "Create Account"}
            </Button>

            <Box sx={{ textAlign: "center" }}>
              <Button
                component="button"
                type="button"
                variant="text"
                onClick={onSwitchToLanding}
                sx={{ textDecoration: "none" }}
              >
                ← Use a different email
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
